/**
 * Open Food Facts client (SPEC 2.2.1).
 *
 * Direct client-side fetch (read-only). No API key. Public CC-licensed data.
 * Two operations:
 *   - searchByName(query, country): returns up to N candidates
 *   - lookupByBarcode(ean): returns one product or null
 *
 * Conventions:
 *   - User-Agent header is required by OFF terms — set "Haus/0.1.0
 *     (haus.jonasrackl.com)" so they can identify us.
 *   - Country bias on search: "world" by default; pass "ch" / "us" to bias
 *     by current Context.
 *   - Rate limit ourselves to ~10 req/min. Calls get a 250ms debounce on
 *     search; barcode is one-shot.
 */

const UA = "Haus/0.1.0 (https://haus.jonasrackl.com)";

// Open Food Facts API canonical fields we care about. OFF mostly uses
// `<key>_100g` for per-100g values; iron / sodium / etc. ship in mg per
// 100g via the canonical key (no _100g suffix sometimes — handle both).
type OffNutriments = {
  "energy-kcal_100g"?: number;
  "energy-kcal_serving"?: number;
  "proteins_100g"?: number;
  "carbohydrates_100g"?: number;
  "sugars_100g"?: number;
  "fat_100g"?: number;
  "saturated-fat_100g"?: number;
  "fiber_100g"?: number;
  "salt_100g"?: number;
  "sodium_100g"?: number;
  "iron_100g"?: number;
  "calcium_100g"?: number;
  "magnesium_100g"?: number;
  "potassium_100g"?: number;
  "zinc_100g"?: number;
  "vitamin-c_100g"?: number;
  "vitamin-d_100g"?: number;
  "vitamin-b6_100g"?: number;
  "vitamin-b12_100g"?: number;
  "folates_100g"?: number;
};

export type OffProduct = {
  code: string;                       // EAN/barcode
  product_name?: string;
  brands?: string;
  countries_tags?: string[];
  serving_size?: string;
  nutriments?: OffNutriments;
  image_thumb_url?: string;
  image_front_url?: string;
};

export type OffSearchResult = {
  products: OffProduct[];
};

const SEARCH_BASE = "https://world.openfoodfacts.org/cgi/search.pl";
const PRODUCT_BASE = "https://world.openfoodfacts.org/api/v2/product";

export async function lookupByBarcode(
  ean: string,
  signal?: AbortSignal,
): Promise<OffProduct | null> {
  const url = new URL(`${PRODUCT_BASE}/${encodeURIComponent(ean)}.json`);
  url.searchParams.set(
    "fields",
    "code,product_name,brands,countries_tags,serving_size,nutriments,image_thumb_url,image_front_url",
  );
  const r = await fetch(url, {
    signal,
    headers: { "User-Agent": UA, Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`OFF HTTP ${r.status}`);
  const j = await r.json();
  if (j.status !== 1) return null;
  return j.product as OffProduct;
}

export async function searchByName(
  query: string,
  options?: { country?: "world" | "ch" | "us"; pageSize?: number; signal?: AbortSignal },
): Promise<OffSearchResult> {
  if (!query.trim()) return { products: [] };
  const country = options?.country ?? "world";
  const pageSize = options?.pageSize ?? 10;

  const url = new URL(SEARCH_BASE);
  url.searchParams.set("search_terms", query);
  url.searchParams.set("search_simple", "1");
  url.searchParams.set("action", "process");
  url.searchParams.set("json", "1");
  url.searchParams.set("page_size", String(pageSize));
  url.searchParams.set(
    "fields",
    "code,product_name,brands,countries_tags,nutriments,image_thumb_url",
  );
  if (country !== "world") {
    url.searchParams.set("tagtype_0", "countries");
    url.searchParams.set("tag_contains_0", "contains");
    url.searchParams.set("tag_0", country);
  }

  const r = await fetch(url, {
    signal: options?.signal,
    headers: { "User-Agent": UA, Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`OFF HTTP ${r.status}`);
  const j = await r.json();
  return { products: (j.products ?? []) as OffProduct[] };
}

/**
 * Convert an OFF product to a Food row insert payload.
 * The return type is intentionally loose so it satisfies the supabase-js
 * insert overload (which requires `Json`-compatible jsonb fields).
 *
 * BW17.7: also fills the flat per-100g columns plus provenance so PantryDetail
 * (and downstream rule firings) can read nutrition without having to walk the
 * jsonb. OFF unit conventions: macros + saturated_fat in g/100g, sodium in
 * g/100g (we convert to mg), iron / calcium / magnesium / potassium / zinc
 * already in mg, vit C / vit B6 / B12 / folates in µg unless OFF chose mg —
 * we trust the value as-is and let the user correct anything off.
 */
export function offToFoodInsert(p: OffProduct, userId: string) {
  const n = p.nutriments ?? {};
  const num = (k: keyof OffNutriments): number | null => {
    const v = n[k];
    return typeof v === "number" && Number.isFinite(v) ? v : null;
  };
  const kcal = num("energy-kcal_100g") ?? 0;
  const protein = num("proteins_100g");
  const carbs = num("carbohydrates_100g");
  const fat = num("fat_100g");
  const fiber = num("fiber_100g");
  // OFF gives sodium in g/100g; convert to mg. Salt -> sodium *1000/2.5
  // when sodium is missing (1g salt ≈ 0.4g sodium = 400mg).
  const sodiumG = num("sodium_100g");
  const saltG = num("salt_100g");
  const naMg =
    sodiumG != null
      ? sodiumG * 1000
      : saltG != null
        ? Math.round(saltG * 400)
        : null;
  return {
    user_id: userId,
    source_id: p.code,
    name: p.product_name?.trim() || `OFF ${p.code}`,
    brand: p.brands?.split(",")[0]?.trim() || null,
    basis: "per_100g" as const,
    kcal: kcal,
    kcal_per_100g: kcal,
    protein_g_per_100g: protein,
    carb_g_per_100g: carbs,
    fat_g_per_100g: fat,
    fiber_g_per_100g: fiber,
    sugar_g_per_100g: num("sugars_100g"),
    saturated_fat_g_per_100g: num("saturated-fat_100g"),
    na_mg_per_100g: naMg,
    fe_mg_per_100g: num("iron_100g"),
    ca_mg_per_100g: num("calcium_100g"),
    mg_mg_per_100g: num("magnesium_100g"),
    k_mg_per_100g: num("potassium_100g"),
    zn_mg_per_100g: num("zinc_100g"),
    vit_c_mg_per_100g: num("vitamin-c_100g"),
    vit_d_ug_per_100g: num("vitamin-d_100g"),
    b6_mg_per_100g: num("vitamin-b6_100g"),
    b12_ug_per_100g: num("vitamin-b12_100g"),
    folate_ug_per_100g: num("folates_100g"),
    image_url: p.image_front_url ?? p.image_thumb_url ?? null,
    nutrition_source: "off" as const,
    nutrition_confidence: "high" as const,
    macros: {
      c: carbs ?? 0,
      p: protein ?? 0,
      f: fat ?? 0,
      fiber: fiber ?? 0,
    },
    micros: {} as Record<string, never>,
    diet_tags: [] as string[],
    store_tags: [] as string[],
    available_locations: (p.countries_tags ?? [])
      .map((t) => t.replace(/^en:/, "").toUpperCase())
      .slice(0, 8),
    source: "off" as const,
    is_favorite: false,
    is_user_overridden: false,
    notes: "",
  };
}
